import { useState, useEffect } from 'react'
import PhotoPreviewOverlay from '../../shared/ui/PhotoPreviewOverlay'
import { formatMinutes, getElapsedMinutes, formatTime } from '../../shared/lib/time'
import OrderHistory from './OrderHistory'
import InspectionHints from '../../shared/ui/InspectionHints'
import BackButton from '../../shared/ui/BackButton'
import { diagnosticStyles } from '../diagnostic/diagnosticStyles'
import { orderViewStyles } from './orderViewStyles'

const statusMap = {
    ok: { label: 'Исправно', color: '#059669', bg: '#D1FAE5' },
    warning: { label: 'Некритичное', color: '#D97706', bg: '#FEF3C7' },
    critical: { label: 'Критичное', color: '#DC2626', bg: '#FEE2E2' },
    na: { label: 'Н/П', color: '#6B7280', bg: '#F3F4F6' },
}

const groupIcons = {
    'Двигатель и тех. жидкости': 'ti-engine',
    'Ходовая часть': 'ti-car',
    'Кузов и электрика': 'ti-bolt',
    'Дополнительный осмотр': 'ti-zoom-check',
}

export default function InProgressOrderView({ order, token, refreshKey = 0, showBack = false, onBack, canEdit = false, onEditClient }) {
    const [results, setResults] = useState([])
    const [allItems, setAllItems] = useState([])
    const [intake, setIntake] = useState({ photos: [], additional_photos: [] })
    const [loading, setLoading] = useState(true)
    const [previewPhoto, setPreviewPhoto] = useState(null)
    const [, setTick] = useState(0)

    useEffect(() => {
        const controller = new AbortController()
        const options = { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
        const readJson = async (response) => {
            if (!response.ok) throw new Error('Не удалось обновить диагностику')
            return response.json()
        }
        Promise.all([
            fetch(`/api/diagnostic/results/${order.id}`, options).then(readJson),
            fetch(`/api/diagnostic/items?orderId=${order.id}`, options).then(readJson),
            fetch(`/api/orders/${order.id}/intake`, options).then(readJson),
        ]).then(([resultsData, itemsData, intakeData]) => {
            setResults(resultsData)
            setAllItems(itemsData)
            setIntake(intakeData)
            setLoading(false)
        }).catch(error => {
            if (error.name !== 'AbortError') console.error(error)
        })
        return () => controller.abort()
    }, [order.id, token, refreshKey])

    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 60000)
        return () => clearInterval(interval)
    }, [])

    const mileageResult = intake.mileage !== null && intake.mileage !== undefined
        ? { comment: String(intake.mileage), photos: intake.photos || [] }
        : null
    const additionalPhotos = intake.additional_photos || []

    const diagnosticItems = allItems
    const totalItems = diagnosticItems.length
    const filledItems = results.filter(r => r.status).length
    const progress = totalItems > 0 ? Math.round((filledItems / totalItems) * 100) : 0

    const waitMinutes = order.in_progress_at
        ? Math.floor((new Date(order.in_progress_at) - new Date(order.created_at)) / 60000)
        : getElapsedMinutes(order.created_at)

    const workMinutes = order.in_progress_at ? getElapsedMinutes(order.in_progress_at) : null
    const isLongWait = waitMinutes !== null && waitMinutes > 30
    const isLongWork = workMinutes !== null && workMinutes > 90

    const statusLabel = order.status === 'open' ? 'Новая' : 'В работе'
    const statusBg = order.status === 'open' ? '#FFF4EF' : '#FEF3C7'
    const statusColor = order.status === 'open' ? '#FF7438' : '#92400E'

    const problemItems = results.filter(r => r.status === 'critical' || r.status === 'warning')
    const okItems = results.filter(r => r.status === 'ok')
    const naItems = results.filter(r => r.status === 'na')
    const filledIds = new Set(results.filter(r => r.status).map(r => r.item_id))
    const pendingItems = diagnosticItems.filter(i => !filledIds.has(i.id))

    const okGroups = [...new Set(okItems.map(r => r.group_name))]
    const naGroups = [...new Set(naItems.map(r => r.group_name))]
    const pendingGroups = [...new Set(pendingItems.map(i => i.group_name))]

    if (loading) return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F5F9' }}>
            <p style={{ color: '#6B7280' }}>Загрузка...</p>
        </div>
    )

    const sideText = (r, short) => r.side && r.side !== 'none' ? (short ? ' · ' : ' (') + (r.side === 'left' ? 'лев.' : 'прав.') + (short ? '' : ')') : ''
    const thumbs = (photos, alt) => photos && photos.length > 0 && (
        <div className="dg-thumbs" style={{ marginBottom: 0 }}>
            {photos.map(photo => (
                <div key={photo.id} className="dg-thumb"><img src={photo.path} alt={alt} onClick={() => setPreviewPhoto(photo.path)} /></div>
            ))}
        </div>
    )
    const sectionHead = (icon, title, count) => (
        <div className="dg-group-head">
            <span className="dg-group-icon"><i className={`ti ${icon}`} aria-hidden="true" /></span>
            <h2>{title}</h2>
            {count !== undefined && <span className="dg-group-count">{count}</span>}
        </div>
    )
    const groupedList = (groups, list, badge, muted, nameOf = r => r.item_name + sideText(r, true)) => (
        <div className="cv-list">
            {groups.map(group => {
                const groupItems = list.filter(r => r.group_name === group)
                return (
                    <div key={group}>
                        <div className="cv-group-row">
                            <span>{groupIcons[group] && <i className={'ti ' + groupIcons[group]} aria-hidden="true" />}{group}</span>
                            {badge}
                        </div>
                        {groupItems.map(r => (
                            <div key={r.id} className="cv-row">
                                <span className="cv-item" data-muted={muted}>{nameOf(r)}</span>
                                {!muted && r.comment && <p className="cv-comment">{r.comment}</p>}
                                {!muted && thumbs(r.photos, 'фото')}
                            </div>
                        ))}
                    </div>
                )
            })}
        </div>
    )
    const timings = [
        { icon: 'ti-file-plus', label: 'Создана', value: formatTime(order.created_at) },
        { icon: 'ti-hourglass', label: order.in_progress_at ? 'Ожидание' : 'Ждёт уже', value: formatMinutes(waitMinutes), tone: isLongWait ? 'red' : 'amber', color: isLongWait ? '#DC2626' : '#D97706' },
        ...(order.in_progress_at ? [
            { icon: 'ti-player-play', label: 'Взята в работу', value: formatTime(order.in_progress_at) },
            { icon: 'ti-tool', label: 'Идёт диагностика', value: formatMinutes(workMinutes), tone: isLongWork ? 'red' : 'green', color: isLongWork ? '#DC2626' : '#059669', live: true },
        ] : []),
    ]
    const people = [
        order.master_name && { icon: 'ti-user', label: 'Мастер', value: order.master_name },
        order.mechanic_name && { icon: 'ti-tool', label: 'Механик', value: order.mechanic_name },
        order.salon_name && { icon: 'ti-building-store', label: 'Салон', value: order.salon_name },
        order.number && { icon: 'ti-file-invoice', label: '№ заказ-наряда', value: order.number },
    ].filter(Boolean)

    return (
        <div className="dg-page">
            <style>{diagnosticStyles + orderViewStyles + '.cv-live{display:inline-block;width:6px;height:6px;margin-left:5px;border-radius:50%;background:currentColor;vertical-align:1px;animation:vibePulse 1.4s ease-in-out infinite}'}</style>

            <PhotoPreviewOverlay src={previewPhoto} onClose={() => setPreviewPhoto(null)} />

            <div className="dg-head">
                <div className="dg-wrap dg-head-inner">
                    <div className="dg-head-row">
                        {showBack && <BackButton onClick={onBack} />}
                        <div className="dg-head-title">
                            <p className="dg-car">{order.car_brand} {order.car_model}</p>
                            <p className="dg-sub">
                                {order.plate && <span className="dg-plate">{order.plate}</span>}
                                <span>{[order.car_year, order.client_name].filter(Boolean).join(' · ')}</span>
                                {canEdit && order.client_phone && <a href={`tel:${String(order.client_phone).replace(/[^\d+]/g, '')}`}><i className="ti ti-phone" aria-hidden="true" />{order.client_phone}</a>}
                            </p>
                        </div>
                        <div className="dg-actions">
                            {canEdit ? (
                                <button type="button" className="dg-action" data-tone="blue" onClick={onEditClient} aria-label="Изменить" title="Изменить данные клиента">
                                    <i className="ti ti-pencil" aria-hidden="true" />
                                    <span className="dg-action-label">Изменить</span>
                                </button>
                            ) : (
                                <span className="dg-badge" style={{ background: statusBg, color: statusColor }}>{statusLabel}</span>
                            )}
                        </div>
                    </div>
                    <div className="dg-progress">
                        <div className="dg-progress-bar" data-full={totalItems > 0 && filledItems >= totalItems}><div style={{ width: progress + '%' }} /></div>
                        <span>{filledItems}/{totalItems}</span>
                    </div>
                </div>
            </div>

            <div className="dg-wrap dg-body" style={{ gap: '12px' }}>

                {/* МАСТЕР / МЕХАНИК / САЛОН */}
                {people.length > 0 && (
                    <div className="cv-info">
                        {people.map(field => (
                            <div key={field.label} className="cv-field">
                                <span className="cv-field-icon"><i className={`ti ${field.icon}`} aria-hidden="true" /></span>
                                <div><p>{field.label}</p><b>{field.value}</b></div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ТАЙМИНГИ */}
                <section className="dg-card">
                    <div className="cv-card-title" style={{ marginBottom: '10px' }}>
                        <span className="dg-step"><i className="ti ti-clock" aria-hidden="true" /></span>
                        <div>
                            <p className="t">Тайминги</p>
                            <p className="s">Обновляются каждую минуту</p>
                        </div>
                    </div>
                    <div className="cv-info">
                        {timings.map(row => (
                            <div key={row.label} className="cv-field" style={{ boxShadow: 'none', background: '#F7F8FA' }}>
                                <span className="cv-field-icon" data-tone={row.tone} style={row.tone ? undefined : { background: 'white', color: '#6B7280' }}><i className={`ti ${row.icon}`} aria-hidden="true" /></span>
                                <div><p>{row.label}</p><b style={{ color: row.color || '#1A1A1A', fontSize: '12px' }}>{row.value}{row.live && <span className="cv-live" aria-hidden="true" />}</b></div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* КОММЕНТАРИЙ МЕХАНИКА */}
                {(order.mechanic_voice_transcript || order.mechanic_comment) && (
                    <section className="dg-card">
                        <p className="cv-quote-label"><i className="ti ti-microphone" aria-hidden="true" />Комментарий механика</p>
                        <p className="cv-quote">{order.mechanic_voice_transcript || order.mechanic_comment}</p>
                    </section>
                )}

                {/* ПОДСКАЗКИ AI */}
                <InspectionHints hints={order.inspection_hints} brand={order.car_brand} model={order.car_model} />

                <OrderHistory orderId={order.id} token={token} refreshKey={refreshKey} />

                {/* 1. ПРИЁМКА */}
                {mileageResult && (
                    <div className="cv-section">
                        {sectionHead('ti-gauge', 'Приёмка')}
                        <div className="dg-card">
                            <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '600', color: '#1A1A1A' }}>Пробег при приёмке</p>
                            <p style={{ margin: mileageResult.photos?.length ? '0 0 10px' : 0, fontSize: '20px', fontWeight: '700', color: '#1A1A1A' }}>
                                {parseInt(mileageResult.comment).toLocaleString('ru-RU')} <span style={{ fontSize: '13px', color: '#9CA3AF', fontWeight: '400' }}>км</span>
                            </p>
                            {thumbs(mileageResult.photos, 'одометр')}
                        </div>
                    </div>
                )}

                {/* 2. ТРЕБУЕТ ВНИМАНИЯ */}
                {problemItems.length > 0 && (
                    <div className="cv-section" data-tone="red">
                        {sectionHead('ti-alert-triangle', 'Требует внимания', problemItems.length)}
                        <div className="cv-list">
                            {problemItems.map(r => (
                                <div key={r.id} className="cv-row" data-status={r.status}>
                                    <div className="cv-row-head">
                                        <span className="cv-row-name">
                                            <i className={'ti ' + (r.status === 'critical' ? 'ti-alert-triangle' : 'ti-alert-circle')} style={{ color: statusMap[r.status] ? statusMap[r.status].color : '#6B7280' }} aria-hidden="true" />
                                            {r.item_name}{sideText(r, false)}
                                        </span>
                                        <span className="cv-pill" style={{ background: statusMap[r.status]?.bg || '#F3F4F6', color: statusMap[r.status] ? statusMap[r.status].color : '#6B7280' }}>
                                            {statusMap[r.status] ? statusMap[r.status].label : ''}
                                        </span>
                                    </div>
                                    {r.comment && <p className="cv-comment" style={{ background: 'white' }}>{r.comment}</p>}
                                    {thumbs(r.photos, 'фото')}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 3. НЕ ЗАПОЛНЕНО */}
                {pendingItems.length > 0 && (
                    <div className="cv-section">
                        {sectionHead('ti-clock', 'Не заполнено', pendingItems.length)}
                        {groupedList(pendingGroups, pendingItems, null, true, item => item.item_name)}
                    </div>
                )}

                {/* 4. ДОПОЛНИТЕЛЬНЫЙ ОСМОТР */}
                {additionalPhotos.length > 0 && (
                    <div className="cv-section">
                        {sectionHead('ti-zoom-check', 'Дополнительный осмотр')}
                        <div className="dg-card">
                            <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '600', color: '#1A1A1A' }}>Прочие элементы</p>
                            {thumbs(additionalPhotos, 'фото')}
                        </div>
                    </div>
                )}

                {/* 5. БЕЗ ДЕФЕКТОВ */}
                {okItems.length > 0 && (
                    <div className="cv-section" data-tone="green">
                        {sectionHead('ti-circle-check', 'Без дефектов', okItems.length)}
                        {groupedList(okGroups, okItems, <span className="cv-pill" style={{ background: '#D1FAE5', color: '#059669' }}>Исправно</span>, false)}
                    </div>
                )}

                {/* 6. НЕ ПРЕДУСМОТРЕНО */}
                {naItems.length > 0 && (
                    <div className="cv-section">
                        {sectionHead('ti-minus', 'Не предусмотрено', naItems.length)}
                        {groupedList(naGroups, naItems, <span className="cv-pill" style={{ background: '#F3F4F6', color: '#9CA3AF' }}>Н/П</span>, true)}
                    </div>
                )}

                {allItems.length === 0 && (
                    <div className="cv-empty">Механик ещё не начал заполнение</div>
                )}

            </div>
        </div>
    )
}
